/**
 * /agriculteurs/aides — Hub des aides aux exploitations agricoles
 *
 * Liste les 7 leviers mobilisables par les exploitants (CEE secteur AGRI,
 * FranceAgriMer, Plan France 2030, PCAE régional, aides locales,
 * optimisation TURPE, exonération CSPE).
 * Chaque carte renvoie vers la page éditoriale dédiée
 * `/agriculteurs/aides/[slug]`.
 */

import { ArrowRight, CheckCircle2 } from 'lucide-react';
import { Container } from '../../../components/layout/Container';
import { Button } from '../../../components/ui/button';
import { BreadcrumbListLd } from '../../../components/seo/jsonld/BreadcrumbList';
import { AIDES_AGRICULTEURS } from '../../../config/aides-agriculteurs';
import { getIcon } from '../../../lib/lucide-icons';

const SITE_URL = 'https://solutionecofrance.fr';

export default function AgriculteursAidesHubPage() {
  return (
    <>
      <BreadcrumbListLd
        items={[
          { name: 'Accueil', url: `${SITE_URL}/` },
          { name: 'Agriculteurs', url: `${SITE_URL}/agriculteurs` },
          { name: 'Aides', url: `${SITE_URL}/agriculteurs/aides` },
        ]}
      />

      {/* ── Hero ── */}
      <section className="relative overflow-hidden bg-gradient-to-b from-stone-50 to-paper pt-12 pb-12 md:pt-16 md:pb-16">
        <div
          aria-hidden
          className="pointer-events-none absolute inset-0 bg-[radial-gradient(ellipse_80%_50%_at_50%_-10%,rgba(16,185,129,0.06),transparent)]"
        />
        <Container size="narrow">
          <nav aria-label="Fil d'Ariane" className="mb-6 text-[12px] text-stone-500">
            <a href="/agriculteurs" className="hover:text-brand-700">Agriculteurs</a>
            <span className="mx-1.5 text-stone-300">/</span>
            <span className="font-medium text-stone-700">Aides</span>
          </nav>

          <span className="inline-flex items-center gap-1.5 rounded-full bg-brand-100 px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.12em] text-brand-800">
            Panorama 2026
          </span>

          <h1 className="mt-4 h-hero-lg">
            Toutes vos aides{' '}
            <span className="bg-gradient-to-r from-brand-600 to-accent-500 bg-clip-text text-transparent">
              pour votre exploitation
            </span>
          </h1>

          <p className="mt-4 text-lg text-stone-600 md:text-xl">
            CEE secteur AGRI, FranceAgriMer, Plan France 2030, PCAE régional, aides locales —
            et deux leviers directs sur vos factures : l&rsquo;optimisation du tarif
            d&rsquo;acheminement (TURPE) et l&rsquo;exonération CSPE. Sept leviers cumulables
            pour vos investissements (serres, élevage, énergie, équipements).
          </p>

          <div className="mt-8 flex flex-wrap gap-3">
            <Button asChild size="lg" variant="primary" className="group">
              <a href="/agriculteurs/diagnostic-cee">
                Diagnostic CEE agricole en 60 sec
                <ArrowRight className="h-4 w-4 transition-transform duration-150 ease-out group-hover/btn:translate-x-0.5" />
              </a>
            </Button>
            <Button asChild size="lg" variant="ghost">
              <a href="/cee/fiches?secteur=AGRI">Catalogue CEE AGRI</a>
            </Button>
          </div>

          <div className="mt-6 flex flex-wrap gap-x-6 gap-y-2">
            {['Gratuit · sans inscription', 'Résultat immédiat', 'Aides 2026 à jour'].map((g) => (
              <span key={g} className="inline-flex items-center gap-1.5 text-[12px] text-stone-500">
                <CheckCircle2 className="h-3.5 w-3.5 shrink-0 text-brand-500" strokeWidth={2} />
                {g}
              </span>
            ))}
          </div>
        </Container>
      </section>

      {/* ── Grid des aides ── */}
      <section className="py-14 md:py-20">
        <Container size="narrow">
          <div className="mb-10">
            <p className="text-[11px] font-bold uppercase tracking-[0.18em] text-stone-400">
              7 leviers cumulables
            </p>
            <h2 className="mt-2 h-section">
              Les aides et économies disponibles pour les exploitations
            </h2>
          </div>

          <div className="grid gap-4 sm:grid-cols-2">
            {AIDES_AGRICULTEURS.map((aide) => {
              const Icon = getIcon(aide.iconName);
              return (
                <a
                  key={aide.slug}
                  href={`/agriculteurs/aides/${aide.slug}`}
                  className="group flex flex-col gap-3 rounded-2xl border border-stone-200 bg-paper p-6 shadow-[0_1px_3px_rgba(10,10,10,.04)] transition-all duration-150 ease-out hover:-translate-y-px hover:border-stone-300 hover:shadow-[0_8px_28px_-8px_rgba(10,10,10,.10)]"
                >
                  <div className="flex items-center justify-between">
                    <span className="inline-flex h-10 w-10 items-center justify-center rounded-lg bg-brand-50 text-brand-700 ring-1 ring-inset ring-brand-200/60 transition-transform duration-150 ease-out group-hover:scale-105">
                      <Icon className="h-5 w-5" strokeWidth={1.7} />
                    </span>
                    <span className="text-[10px] font-bold uppercase tracking-[0.16em] text-stone-400">
                      {aide.eyebrow}
                    </span>
                  </div>

                  <div>
                    <p className="font-display text-xl font-bold text-ink">{aide.title}</p>
                    <p className="mt-1 text-[13px] text-stone-500">{aide.amountLabel}</p>
                  </div>

                  <p className="line-clamp-2 text-[13px] leading-relaxed text-stone-600">
                    {aide.lead}
                  </p>

                  <div className="mt-auto flex items-center justify-between pt-2">
                    <span className="bg-gradient-to-br from-accent-700 to-brand-600 bg-clip-text text-lg font-bold tracking-tight text-transparent">
                      {aide.amount}
                    </span>
                    <span className="inline-flex items-center gap-1 text-[12px] font-semibold text-brand-700 transition-transform duration-150 ease-out group-hover:translate-x-0.5">
                      En savoir plus
                      <ArrowRight className="h-3 w-3" strokeWidth={2.4} />
                    </span>
                  </div>
                </a>
              );
            })}
          </div>
        </Container>
      </section>

      {/* ── CTA final ── */}
      <section className="pb-20">
        <Container size="narrow">
          <div className="rounded-2xl bg-gradient-to-br from-ink via-stone-900 to-ink p-8 text-center md:p-12">
            <p className="text-[11px] font-bold uppercase tracking-[0.18em] text-stone-400">
              Diagnostic personnalisé
            </p>
            <h2 className="mt-3 h-section on-dark">
              Quelle aide pour votre exploitation ?
            </h2>
            <p className="mt-3 text-stone-400">
              On identifie les fiches CEE secteur AGRI qui s'appliquent chez vous, on vérifie le
              PCAE de votre Région et on regarde quels appels FranceAgriMer sont ouverts.
            </p>
            <div className="mt-6 flex flex-wrap justify-center gap-3">
              <Button asChild size="lg" variant="primary">
                <a href="/agriculteurs/diagnostic-cee">
                  Lancer le diagnostic agricole
                  <ArrowRight className="h-4 w-4" />
                </a>
              </Button>
              <Button asChild size="lg" variant="outline">
                <a href="/contact?source=agriculteurs">Être rappelé(e)</a>
              </Button>
            </div>
          </div>
        </Container>
      </section>
    </>
  );
}
